/**
 * The numbers El Tablero shows (TASKS T65, SPEC §8).
 *
 * Pure: no clock, no store, no DOM. `now` and the records are arguments, which is what makes every
 * display rule below assertable without a browser.
 *
 * ## Four rules that shape every function here
 *
 *  1. **Never per day.** Every window is at least a week (`MIN_WINDOW_DAYS`). Daily granularity
 *     manufactures streak psychology even with no streak counter, and a bad night must never render
 *     as a red mark (SPEC §8.1).
 *  2. **`null` is not zero.** "Nothing measured" and "measured, and it was none" are different
 *     claims, and conflating them is how a tracker starts lying. Every metric returns `null` rather
 *     than a zero it cannot justify.
 *  3. **Measured and remembered are never added together.** `SessionRecord.appMeasuredMinutes` is a
 *     measurement; `ManualEntry.minutes` is a memory. They are reported side by side, always
 *     labelled, and no field anywhere is their sum (DoD-12).
 *  4. **Nothing here judges the person.** These are workloads and rates. The words "good", "bad",
 *     "behind" and "on track" do not appear, and no function returns a grade.
 */

import type { Attempt, ManualComponent, ManualEntry, SessionRecord } from '../store/schema';
import type { Tier } from '../state/gamestate';
import { advancesInterval } from '../scheduler/gate';
import { GRAMMAR_CAP, isGrammarTier } from '../round/quota';

export const DAY_MS = 86_400_000;

/** No window is shorter than this, anywhere, in any view (SPEC §8.1, DoD-8). */
export const MIN_WINDOW_DAYS = 7;

/** The default reporting window. */
export const DEFAULT_WINDOW_DAYS = 28;

/** The grammar share's window is shorter, because the cap it is measured against is (invariant 9). */
export const GRAMMAR_WINDOW_DAYS = 7;

/**
 * A component with no entry for this long renders as "not logged" (SPEC §8.4, DoD-9).
 *
 * Never as zero. "He did no conversation practice" and "he did not write any down" are different
 * facts, and the app can only ever observe the second.
 */
export const NOT_LOGGED_AFTER_DAYS = 14;

/** Anything narrower than a week is a bug, so it is refused rather than rendered. */
export function assertWindow(days: number): number {
  if (!Number.isFinite(days) || days < MIN_WINDOW_DAYS) {
    throw new RangeError(
      `window of ${days} days is below the ${MIN_WINDOW_DAYS}-day floor (SPEC §8.1): no view may ` +
        'show per-day granularity',
    );
  }
  return days;
}

function withinWindow<T extends { at: number }>(
  records: readonly T[],
  now: number,
  days: number,
): T[] {
  const from = now - assertWindow(days) * DAY_MS;
  return records.filter((record) => record.at > from && record.at <= now);
}

// ---------------------------------------------------------------------------------------------
// Retention
// ---------------------------------------------------------------------------------------------

export interface Retention {
  /** 0..1, or null when nothing in the window could be measured. Never 0 for "no data". */
  rate: number | null;
  /** Attempts the rate is drawn from. Shown beside it, so a 100% off three items reads honestly. */
  sample: number;
  correct: number;
}

/**
 * "% of due items produced correctly on **first attempt**" (SPEC §8.2).
 *
 * Four filters, and each excludes something that would flatter the number:
 *
 *  - **Due reviews only.** A new word answered right minutes after being introduced is not evidence
 *    of retention; including it would make an intake-heavy week look like a good one.
 *  - **Production only, unhinted, first attempt** — `advancesInterval`, the same predicate the
 *    scheduler gates on. If those two definitions could drift, one of them would be wrong.
 */
export function retention(
  attempts: readonly Attempt[],
  now: number,
  days: number = DEFAULT_WINDOW_DAYS,
): Retention {
  const window = withinWindow(attempts, now, days).filter(
    (attempt) => attempt.wasDueReview && advancesInterval(attempt),
  );
  const correct = window.filter((attempt) => attempt.correct).length;
  return {
    rate: window.length === 0 ? null : correct / window.length,
    sample: window.length,
    correct,
  };
}

/** Retention per tier, for the drilldown. `null` where a tier has nothing in the window. */
export function perTierAccuracy(
  attempts: readonly Attempt[],
  now: number,
  days: number = DEFAULT_WINDOW_DAYS,
): Record<'1' | '2' | '3' | '4' | '5' | '6', number | null> {
  const window = withinWindow(attempts, now, days).filter(advancesInterval);
  const out = {} as Record<'1' | '2' | '3' | '4' | '5' | '6', number | null>;
  for (const tier of [1, 2, 3, 4, 5, 6] as Tier[]) {
    const forTier = window.filter((attempt) => attempt.tier === tier);
    const key = String(tier) as '1';
    out[key] = forTier.length === 0 ? null : forTier.filter((a) => a.correct).length / forTier.length;
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Balance
// ---------------------------------------------------------------------------------------------

/** Which side of the balance each manually logged component falls on (BRIEF <progress_tracker>). */
const INPUT_COMPONENTS: ReadonlySet<ManualComponent> = new Set(['course', 'input']);
const OUTPUT_COMPONENTS: ReadonlySet<ManualComponent> = new Set(['conversation', 'narration']);

export interface Balance {
  /** Study and comprehensible input. All remembered — the app observes none of it. */
  inputMinutes: number;
  /**
   * Production and retrieval, kept in its two halves.
   *
   * Never added into one number *anywhere it is shown* (DoD-12): one is a measurement and the other
   * is a memory, and a single figure would present them as equally reliable. The ratio below does
   * combine them, which is the one place it is unavoidable — see `ratio`.
   */
  outputMeasuredMinutes: number;
  outputLoggedMinutes: number;
  /**
   * input : output, or null when there is nothing on one side.
   *
   * This is the one figure that necessarily spans measured and remembered minutes, because the game
   * itself is the fifth plan component and belongs on the production side (BRIEF: "the game observes
   * only one of five plan components"). It is a *ratio*, not a minutes total — the components stay
   * separate above it, and the tracker renders them beside it. See DECISIONS D-207.
   */
  ratio: number | null;
}

export function balance(
  manual: readonly ManualEntry[],
  sessions: readonly SessionRecord[],
  now: number,
  days: number = DEFAULT_WINDOW_DAYS,
): Balance {
  const entries = withinWindow(manual, now, days);
  const played = sessions.filter(
    (session) => session.startedAt > now - assertWindow(days) * DAY_MS && session.startedAt <= now,
  );

  const inputMinutes = sum(entries.filter((e) => INPUT_COMPONENTS.has(e.component)));
  const outputLoggedMinutes = sum(entries.filter((e) => OUTPUT_COMPONENTS.has(e.component)));
  const outputMeasuredMinutes = played.reduce(
    (total, session) => total + session.appMeasuredMinutes,
    0,
  );

  const output = outputMeasuredMinutes + outputLoggedMinutes;
  return {
    inputMinutes,
    outputMeasuredMinutes,
    outputLoggedMinutes,
    ratio: inputMinutes === 0 || output === 0 ? null : inputMinutes / output,
  };
}

function sum(entries: readonly ManualEntry[]): number {
  return entries.reduce((total, entry) => total + entry.minutes, 0);
}

// ---------------------------------------------------------------------------------------------
// What has not been logged
// ---------------------------------------------------------------------------------------------

export interface ComponentStatus {
  component: ManualComponent;
  /** Minutes in the window, or null when the component is `notLogged`. Never 0 for "no entry". */
  minutes: number | null;
  /** Nothing written down for `NOT_LOGGED_AFTER_DAYS`. Not the same as "he did none". */
  notLogged: boolean;
  /** Epoch ms of the most recent entry, ever — not only within the window. */
  lastLoggedAt: number | null;
}

/**
 * Each manual component's standing (DoD-9).
 *
 * `notLogged` is computed from the **last entry ever**, not from the window: a component logged
 * three weeks ago is not "not logged", it is stale, and the tracker says so with a date rather than
 * by pretending nothing was ever written.
 */
export function componentStatus(
  manual: readonly ManualEntry[],
  component: ManualComponent,
  now: number,
  days: number = DEFAULT_WINDOW_DAYS,
): ComponentStatus {
  const mine = manual.filter((entry) => entry.component === component);
  const lastLoggedAt = mine.reduce<number | null>(
    (latest, entry) => (latest === null || entry.at > latest ? entry.at : latest),
    null,
  );
  const notLogged =
    lastLoggedAt === null || now - lastLoggedAt > NOT_LOGGED_AFTER_DAYS * DAY_MS;

  return {
    component,
    minutes: notLogged ? null : sum(withinWindow(mine, now, days)),
    notLogged,
    lastLoggedAt,
  };
}

// ---------------------------------------------------------------------------------------------
// Grammar share
// ---------------------------------------------------------------------------------------------

export interface GrammarShareMetric {
  /** 0..1, or null when nothing was attempted in the window. */
  share: number | null;
  /** The cap it is read against. Carried so no view has to restate 0.25. */
  cap: number;
  /** Above the cap. Expected and not a defect when it is review-driven (SPEC §6.3). */
  overCap: boolean;
  /**
   * The share is above the cap *only* because of due reviews.
   *
   * Reported so the tracker can say `review-driven` rather than implying over-investment. Reviewing
   * an already-learned grammar item is spaced retrieval doing its job; the cap governs intake.
   */
  reviewDriven: boolean;
  sample: number;
}

export function grammarShareMetric(
  attempts: readonly Attempt[],
  now: number,
  days: number = GRAMMAR_WINDOW_DAYS,
  cap: number = GRAMMAR_CAP,
): GrammarShareMetric {
  const window = withinWindow(attempts, now, days);
  if (window.length === 0) {
    return { share: null, cap, overCap: false, reviewDriven: false, sample: 0 };
  }

  const grammar = window.filter((attempt) => isGrammarTier(attempt.tier));
  const share = grammar.length / window.length;
  const discretionary = grammar.filter((attempt) => !attempt.wasDueReview).length;

  return {
    share,
    cap,
    overCap: share > cap,
    // Every grammar attempt in the window was a due review, so nothing was *chosen*.
    reviewDriven: share > cap && discretionary / window.length <= cap,
    sample: window.length,
  };
}

// ---------------------------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------------------------

export interface SessionMetrics {
  /** Minutes, or null when nothing was played in the window. */
  medianMinutes: number | null;
  /** Sessions per seven days across the window, or null when nothing was played. */
  perWeek: number | null;
  count: number;
  /**
   * Days in the window with at least one session.
   *
   * A **count**, never a calendar: which days is per-day granularity and is forbidden (SPEC §8.1).
   * This exists for the proficiency diagnostic's "days with zero reviews" line (SPEC §8.6).
   */
  daysActive: number;
}

/** Median rather than mean: one ninety-minute evening must not redraw a month of six-minute feeds. */
export function sessionMetrics(
  sessions: readonly SessionRecord[],
  now: number,
  days: number = DEFAULT_WINDOW_DAYS,
): SessionMetrics {
  const from = now - assertWindow(days) * DAY_MS;
  const window = sessions.filter(
    (session) => session.startedAt > from && session.startedAt <= now,
  );
  if (window.length === 0) {
    return { medianMinutes: null, perWeek: null, count: 0, daysActive: 0 };
  }

  const lengths = window.map((session) => session.appMeasuredMinutes).sort((a, b) => a - b);
  const middle = Math.floor(lengths.length / 2);
  const medianMinutes =
    lengths.length % 2 === 1 ? lengths[middle]! : (lengths[middle - 1]! + lengths[middle]!) / 2;

  const activeDays = new Set(
    window.map((session) => Math.floor((session.startedAt - from) / DAY_MS)),
  );

  return {
    medianMinutes,
    perWeek: (window.length / days) * 7,
    count: window.length,
    daysActive: activeDays.size,
  };
}

// ---------------------------------------------------------------------------------------------
// Productive vocabulary
// ---------------------------------------------------------------------------------------------

/** An interval this long counts a word as productive, for the proxy (SPEC §8.5). */
export const PRODUCTIVE_INTERVAL_DAYS = 21;

/**
 * Items at a long interval — the **proxy** for productive vocabulary.
 *
 * A count of items the scheduler has stopped worrying about, and nothing more. It is labelled a
 * proxy everywhere it is shown (SPEC §8.5) because it measures the schedule's confidence, not the
 * learner's ability to produce the word in conversation, and the two come apart exactly where it
 * would matter most.
 */
export function productiveVocabulary(
  items: readonly { memory: { stability: number; state: string } }[],
  minIntervalDays: number = PRODUCTIVE_INTERVAL_DAYS,
): number {
  return items.filter(
    (item) => item.memory.state === 'review' && item.memory.stability >= minIntervalDays,
  ).length;
}
